import { Link } from 'react-router-dom';
import { FOOTER_COLUMNS, isDirectFileLink } from '@/data/navigation.js';
import { LOGO_SRC } from '@/components/layout/SiteLogo.jsx';
import { useConferenceDates } from '@/context/ConferenceConfigContext.jsx';

const SOCIAL_LINKS = [
  {
    id: 'linkedin',
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/g-r-scholastic-global/posts/?feedView=all',
    className: 'border-transparent bg-[#0A66C2] text-white hover:bg-[#004182]',
    icon: 'linkedin',
  },
  {
    id: 'x',
    label: 'X (Twitter)',
    href: 'https://x.com/isbm_conf',
    className: 'border-transparent bg-black text-white hover:bg-[#1a1a1a]',
    icon: 'x',
  },
  {
    id: 'youtube',
    label: 'YouTube',
    href: 'https://www.youtube.com/@worldconferenceonisbm2635',
    className: 'border-transparent bg-white hover:bg-white',
    icon: 'youtube',
  },
  {
    id: 'nn',
    label: 'GR Connect',
    href: 'https://gr-connect.org',
    className: 'border-white/15 bg-white',
    icon: 'nn',
  },
];

function LinkedInIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.74l7.727-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"
      />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true" focusable="false">
      <path
        fill="#FF0000"
        d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
      />
    </svg>
  );
}

function SocialIcon({ icon }) {
  if (icon === 'linkedin') return <LinkedInIcon />;
  if (icon === 'x') return <XIcon />;
  if (icon === 'youtube') return <YouTubeIcon />;
  if (icon === 'nn') {
    return (
      <img
        src="/assets/images/logo/NN.jpeg"
        alt=""
        className="h-6 w-6 object-contain invert"
        loading="lazy"
      />
    );
  }
  return null;
}

export function Footer() {
  const { displayRange, cityCountry } = useConferenceDates();
  const meta = [displayRange, cityCountry, 'Hybrid'].filter(Boolean).join(' · ');

  return (
    <footer
      className="relative bg-[#061e2f] text-white/55 border-t border-white/20 pt-14 sm:pt-16 lg:pt-20"
      role="contentinfo"
    >
      <div className="container">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-12 xl:gap-[4.5rem] pb-10 lg:pb-14 border-b border-white/10">
          <div className="flex flex-col sm:max-w-sm shrink-0">
            <div className="inline-flex items-center gap-4 w-fit mb-5">
              <Link
                to="/"
                aria-label="ISBM 2026 home"
                className="inline-flex items-center no-tap-highlight shrink-0"
              >
                <img
                  src={LOGO_SRC}
                  alt="ISBM Bangkok 2026"
                  width={160}
                  height={48}
                  className="block h-14 sm:h-16 w-auto max-w-[15.5rem] sm:max-w-[18rem] object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </Link>

              <span
                aria-hidden="true"
                className="self-stretch w-px my-1.5 bg-white/30 shrink-0"
              />

              <a
                href="https://www.gr-conferences.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GR Conferences"
                className="inline-flex no-tap-highlight shrink-0"
              >
                <img
                  src="/assets/images/logo/new-gr-logo.png"
                  alt="GR Conferences"
                  width={128}
                  height={128}
                  className="block h-20 sm:h-24 w-auto max-w-[5.75rem] sm:max-w-[6.75rem] object-contain invert"
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </div>

            <p className="text-sm text-white/50 leading-[1.7] m-0 mb-3">
              Driving The Future Of Knowledge
            </p>
            <p className="font-mono text-[0.7rem] font-medium uppercase tracking-[0.14em] text-white/90 m-0 mb-5">
              {meta}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.id}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border transition hover:-translate-y-0.5 hover:shadow-[0_8px_18px_rgba(0,0,0,0.25)] ${s.className}`}
                >
                  <SocialIcon icon={s.icon} />
                </a>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-7 gap-y-8 min-[640px]:grid-cols-3 min-[1200px]:grid-cols-4 sm:gap-x-6 min-[1200px]:gap-x-5 lg:flex-1">
            {FOOTER_COLUMNS.map((col) => (
              <div key={col.title} className="min-w-0">
                <h3 className="font-display text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-white/45 mb-4">
                  {col.title}
                </h3>
                <ul className="space-y-3">
                  {col.links.map((l) => {
                    const linkClass =
                      'text-sm text-white/[0.72] hover:text-[#B8E6F0] transition-colors break-words';
                    const newTabProps =
                      col.openInNewTab || l.openInNewTab
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {};

                    if (isDirectFileLink(l.to)) {
                      return (
                        <li key={l.label}>
                          <a href={l.to} className={linkClass} {...newTabProps}>
                            {l.label}
                          </a>
                        </li>
                      );
                    }

                    return (
                      <li key={l.label}>
                        <Link to={l.to} className={linkClass} {...newTabProps}>
                          {l.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-[1.35rem]">
        <div className="container flex flex-col items-center justify-between gap-3 text-center lg:flex-row lg:text-left">
          <p className="text-xs text-white/40 m-0 order-2 lg:order-none">
            © {new Date().getFullYear()} ISBM Bangkok. All rights reserved.
          </p>
          <p className="font-display italic text-xs text-white/[0.38] m-0 order-1 lg:order-none text-center">
            This Doesn&apos;t Happen Overnight but Leads to a Brand.
          </p>
          <p className="text-xs text-white/40 m-0 order-3 lg:order-none flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1">
            <Link to="/participation-policy" className="hover:text-white transition-colors">
              Participation Policy
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors">
              Terms &amp; Conditions
            </Link>
            <span aria-hidden="true">·</span>
            <span>Developed &amp; Maintained by GR SCHOLASTIC</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
